'use client';
import { ReactNode } from 'react';
import { displayTierName } from '@/util/product_client_safe';
import cn from 'classnames';
import Image from 'next/image';

import Text from '@/components/common/Text/Text';
import RichText from '@/components/common/RichText/RichText';
import Link from '@/components/common/Link/Link';
import Button from '@/components/common/Button/Button';

import { SubscriptionProduct, SubscriptionTitle } from '@/types/Subscription';
import { pluralize } from '@/util/string';

import { useLoggedInUser } from '@/components/hook/user';

import Star from 'public/img/icon/star.svg';
import Heart from 'public/img/icon/heart.svg';
import Checkmark from 'public/img/icon/checkmark-pink.svg';

import './PlanCard.style.scss';
import { getTierIconByName } from '@/util/subscription_client';

type PlanCardProps = {
  className?: string;
  planData: SubscriptionProduct;
  selected?: boolean;
  disabled?: boolean;
  onPlanSelected?: (selectedPlan: SubscriptionProduct) => void;
  ctaCopy?: string;
};

/** Retrieves the mini-banner depending on the name of the plan */
function getMiniBannerForPlan(plan: SubscriptionTitle): ReactNode {
  switch (plan) {
    case 'Classique': {
      return (
        <div className='miniBanner-content'>
          <Image
            className='miniBanner-icon'
            src={Heart}
            height={12}
            width={12}
            alt='Heart Icon'
          />
          <Text
            className='miniBanner-text'
            variant='title-xxs-500'
            uppercase
          >
            Most Popular
          </Text>
        </div>
      );
    }

    case 'Classique+': {
      return (
        <div className='miniBanner-content'>
          <Image
            className='miniBanner-icon'
            src={Star}
            height={12}
            width={12}
            alt='Star Icon'
          />
          <Text
            className='miniBanner-text'
            variant='title-xxs-500'
            uppercase
          >
            New Tier
          </Text>
        </div>
      );
    }

    default: {
      return null;
    }
  }
}

function getPlanDisplayName(planName: SubscriptionTitle): ReactNode {
  if (planName === 'Couture+') {
    return (
      <>
        Couture <span className='plaintext'>+</span>
      </>
    );
  } else if (planName === 'Classique+') {
    return (
      <>
        Classique <span className='plaintext'>+</span>
      </>
    );
  }

  return displayTierName(planName);
}

function PlanCard(props: PlanCardProps) {
  const {
    className = '',
    planData,
    selected,
    disabled,
    onPlanSelected,
    ctaCopy = 'Apply Now'
  } = props;

  const user = useLoggedInUser();
  const miniBanner = getMiniBannerForPlan(planData.name as SubscriptionTitle);
  let planHref;

  if (user) {
    planHref = '/account';
  } else {
    planHref = `/apply/account?subscription=${planData.slug}`;
  }

  return (
    <div
      className={cn(
        'PlanCard',
        planData.slug,
        { 'card-with-banner': !!miniBanner },
        className
      )}
    >
      <div className={cn('PlanCard-content', { selected })}>
        <div className='PlanCard-title'>
          <Image
            className={cn('PlanCard-icon')}
            src={
              getTierIconByName(planData.name as SubscriptionTitle) as string
            }
            alt={`${displayTierName(planData.name)} Icon`}
            height={30}
            width={30}
          />
          <Text
            variant='title-xl'
            italic
          >
            {getPlanDisplayName(planData.name as SubscriptionTitle)}
          </Text>
        </div>
        <div className='PlanCard-closet'>
          <div className='PlanCard-closet-list'>
            <Text
              variant='title-xxs-500'
              uppercase
            >
              {planData.allowed_borrowed_items}{' '}
              {pluralize('item', planData.allowed_borrowed_items)} at a time
            </Text>
            {planData.closetAccess.map((closet, index) => {
              if (planData.name === 'Classique+' && index === 0) {
                return (
                  <Text
                    key={index}
                    className='PlanCard-closet-listItem'
                    variant='text-m'
                  >
                    2 from the {closet} closet
                  </Text>
                );
              } else if (planData.name === 'Classique+' && index === 1) {
                return null;
              }

              return (
                <Text
                  key={index}
                  className='PlanCard-closet-listItem'
                  variant='text-m'
                >
                  {planData.name === 'Couture+' && '1'} from the {closet} closet
                  {planData.closetAccess[index + 1] && ' +'}
                </Text>
              );
            })}
          </div>
          <RichText className='PlanCard-closet-access'>
            {planData.descriptionHTML}
          </RichText>

          <div className='PlanCard-itemTypeList'>
            {planData.bulletPointCopy.map((bulletPoint, index) => {
              return (
                <div
                  className='PlanCard-itemTypeList-bullet'
                  key={index}
                >
                  <Image
                    className='PlanCard-itemTypeList-checkmark'
                    src={Checkmark}
                    width={18}
                    height={16}
                    alt='Checkmark icon'
                  />
                  <Text
                    className='PlanCard-itemTypeList-text'
                    variant='text-m'
                  >
                    {bulletPoint}
                  </Text>
                </div>
              );
            })}
          </div>
          <div className='PlanCard-price'>
            <div className='PlanCard-price-numbers'>
              <Text
                className='PlanCard-price-big'
                variant='title-xl'
                italic
              >
                {/* Remove cents via .split() */}
                {planData.price.asFormattedString.split('.')[0]}
              </Text>
              <Text
                className='PlanCard-price-month'
                variant='text-s'
              >
                / month
              </Text>
            </div>
            {planData.price.savings && (
              <Text
                className='PlanCard-savings'
                variant='text-m'
              >
                {planData.price.savings}
              </Text>
            )}
          </div>

          {onPlanSelected ? (
            <Button
              className={cn('PlanCard-cta', {
                selected,
                disabled
              })}
              onClick={() => {
                onPlanSelected(planData);
              }}
            >
              {ctaCopy}
            </Button>
          ) : (
            <Link
              className={cn('Button dark large PlanCard-cta', {
                selected,
                disabled
              })}
              href={planHref}
            >
              {ctaCopy}
            </Link>
          )}
        </div>
      </div>

      {miniBanner && <div className='miniBanner'>{miniBanner}</div>}
    </div>
  );
}

export default PlanCard;
